import React from 'react';
import { Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { IconInfoCircle } from '@tabler/icons-react';
import TooltipIcon from './TooltipIcon';
import { selectStyles, OPTIONS_REF_MAP } from '../constants/selectStyles';

// =============================================================================
// DYNAMIC CONFIG FIELD RENDERER
// =============================================================================
const DynamicConfigField = ({ field, value, onChange, allValues, TIME_OPTIONS_LIST }) => {
  // Check if field should be shown based on showIf condition
  if (field.showIf) {
    const conditionMet = Object.entries(field.showIf).every(
      ([key, expectedValue]) => allValues?.[key] === expectedValue
    );
    if (!conditionMet) return null;
  }

  const currentValue = value ?? field.default;

  switch (field.type) {
    case 'boolean':
      return (
        <Form.Group className="mb-3">
          <div className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id={`field-${field.key}`}
              label={field.label}
              checked={currentValue}
              onChange={(e) => onChange(field.key, e.target.checked)}
            />
            <TooltipIcon tooltip={field.tooltip} />
          </div>
        </Form.Group>
      );

    case 'number':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">
            {field.label}
            <TooltipIcon tooltip={field.tooltip} />
          </Form.Label>
          <Form.Control
            type="number"
            className="afc-form-control"
            value={currentValue}
            min={field.min}
            max={field.max}
            onChange={(e) => onChange(field.key, parseInt(e.target.value) || 0)}
          />
          {field.min !== undefined && field.max !== undefined && (
            <Form.Text className="text-muted">
              Entre {field.min} e {field.max}
            </Form.Text>
          )}
        </Form.Group>
      );

    case 'select':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">
            {field.label}
            <TooltipIcon tooltip={field.tooltip} />
          </Form.Label>
          <Form.Select
            className="afc-form-control"
            value={currentValue}
            onChange={(e) => onChange(field.key, e.target.value)}
          >
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {field.optionLabels?.[opt] || opt}
              </option>
            ))}
          </Form.Select>
          {field.infoAlert && (Array.isArray(field.infoAlert.condition) ? field.infoAlert.condition.includes(currentValue) : currentValue === field.infoAlert.condition) && (
            <Alert variant="info" className="mt-2 mb-0 py-2 d-flex align-items-center gap-2">
              <IconInfoCircle size={18} />
              <small>{field.infoAlert.message}</small>
            </Alert>
          )}
        </Form.Group>
      );

    case 'multiselect':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">{field.label}</Form.Label>
          <div className="afc-checkbox-group">
            {field.options?.map((opt) => (
              <Form.Check
                key={opt}
                type="checkbox"
                id={`${field.key}-${opt}`}
                label={field.optionLabels?.[opt] || opt}
                checked={(currentValue ?? []).includes(opt)}
                onChange={(e) => {
                  const current = currentValue ?? [];
                  const updated = e.target.checked
                    ? [...current, opt]
                    : current.filter((v) => v !== opt);
                  onChange(field.key, updated);
                }}
              />
            ))}
          </div>
        </Form.Group>
      );

    case 'text':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">{field.label}</Form.Label>
          <Form.Control
            type="text"
            className="afc-form-control"
            value={currentValue || ''}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        </Form.Group>
      );

    case 'password':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">{field.label}</Form.Label>
          <Form.Control
            type="password"
            className="afc-form-control"
            value={currentValue || ''}
            placeholder={field.placeholder || '••••••••'}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        </Form.Group>
      );

    case 'textarea':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">
            {field.label}
            <TooltipIcon tooltip={field.tooltip} />
            {field.helpModal && (
              <span
                className="d-inline-flex align-items-center ms-1"
                style={{ cursor: 'pointer', color: '#6366f1' }}
                onClick={() => onChange(`_showHelp_${field.helpModal}`, true)}
                title="Ver explicação detalhada"
              >
                <IconInfoCircle size={16} />
              </span>
            )}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            className="afc-form-control"
            value={currentValue || ''}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        </Form.Group>
      );

    case 'time':
      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">{field.label}</Form.Label>
          <Form.Select
            className="afc-form-control"
            value={currentValue}
            onChange={(e) => onChange(field.key, e.target.value)}
          >
            {(TIME_OPTIONS_LIST || []).map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );

    case 'grouped_multiselect': {
      // Get options from reference map
      const groupedOptions = field.optionsRef ? OPTIONS_REF_MAP[field.optionsRef] : [];

      // Convert current value to react-select format
      const selectedValues = (currentValue ?? []).map((val) => {
        // Find the option in grouped options
        for (const group of groupedOptions) {
          const opt = group.options.find((o) => o.value === val);
          if (opt) return opt;
        }
        return { value: val, label: val };
      });

      return (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">
            {field.label}
            <TooltipIcon tooltip={field.tooltip} />
          </Form.Label>
          <Select
            isMulti
            options={groupedOptions}
            value={selectedValues}
            onChange={(selected) => {
              const values = selected ? selected.map((s) => s.value) : [];
              onChange(field.key, values);
            }}
            placeholder="Selecione os campos..."
            noOptionsMessage={() => 'Nenhum campo encontrado'}
            styles={selectStyles}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isClearable
            isSearchable
          />
          {field.description && (
            <Form.Text className="text-muted">{field.description}</Form.Text>
          )}
        </Form.Group>
      );
    }

    case 'section':
      return (
        <div className="mt-3 mb-2 pb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <strong style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{field.label}</strong>
          {field.description && (
            <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>{field.description}</small>
          )}
        </div>
      );

    default:
      return null;
  }
};

export default DynamicConfigField;
